import React, { Component } from 'react';
import { AnimatedButton } from '@trlogic/ui-pack';
import { TransactionJsonModel, TransactionVariableJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import FileUploader, { UploadProgress } from './FileUploader';
import { apiUrl, InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import EnvironmentUtil from '../../util/EnvironmentUtil';
import RoleHelper from '../../RoleHelper';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import HttpMethod from '../../constant/HttpMethod';
import HttpClient from '../../gateway/HttpClient';
import GatewayContainer from '../../config/GatewayContainer';
import GatewayNames from '../../constant/GatewayNames';
import LanguageUtil from '../../util/LanguageUtil';

class SandboxJsonContainer extends Component<Props, State> {
  public state = new State();

  private httpClient: HttpClient = GatewayContainer.getInstance().get(GatewayNames.API_CLIENT);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render() {
    const { selectedTransaction, percent, progress, file, selectedTransactionVariable } = this.state;
    return (
      <div className={'flex flex-col w-full h-full py-5 px-5 gap-y-2'}>
        <div className={'flex items-center justify-between gap-x-5 gap-y-5'}>
          <div className={'flex gap-x-5 items-center w-3/4 '}>
            <div className={'flex items-center w-full'}>
              <FormicaSearchSelectBox
                id={'transaction'}
                disabled={!RoleHelper.canEdit('SandboxJson')}
                placeholder={LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' })}
                rounded
                options={this.prepareTransactionSelectBox()}
                value={selectedTransaction?.name}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeSelectedTransaction(e.value)}
              />
            </div>
            <div className={'flex items-center w-full'}>
              <FormicaSearchSelectBox
                rounded
                disabled={!RoleHelper.canEdit('SandboxJson')}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.key', defaultMessage: 'Key' })}
                options={this.prepareTransactionVariables()}
                value={selectedTransactionVariable?.name}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransactionVariable(e.value)}
              />
            </div>
          </div>
          <div className={'flex gap-x-5 items-center'}>
            <AnimatedButton
              disabled={!RoleHelper.canEdit('SandboxJson') || selectedTransaction == undefined || selectedTransactionVariable == undefined || file == undefined}
              icon={'deploy'}
              customType={'primary'}
              text={LanguageUtil.get({ id: 'common.button.send', defaultMessage: 'Send' })}
              onClick={this.uploadFile}
            />
          </div>
        </div>
        <div className={'flex flex-col gap-y-2 items-center justify-center w-full h-full border-2 border-dashed'}>
          <FileUploader
            disabled={!RoleHelper.canEdit('SandboxJson')}
            acceptedFiles={['application/json', 'text/json']}
            file={file}
            percent={percent}
            progress={progress}
            onChangeFile={this.onChangeFile}
            maxBytes={this.environmentUtil.getStorageMaxSize()}
          />
        </div>
      </div>
    );
  }

  private prepareTransactionSelectBox = () => {
    const { transactions } = this.props;
    return transactions.map((transaction: TransactionJsonModel) => {
      return {
        label: transaction.name,
        value: transaction.name,
      };
    });
  };

  private prepareTransactionVariables = () => {
    const { selectedTransaction } = this.state;
    if (selectedTransaction) {
      return selectedTransaction.variables.map((variable: any) => {
        return {
          label: variable.name,
          value: variable.name,
        };
      });
    }
    return [];
  };

  private onChangeSelectedTransaction = (name: string) => {
    const { transactions } = this.props;
    const selectedTransaction = transactions.find((transaction: TransactionJsonModel) => transaction.name === name);
    if (selectedTransaction) {
      this.setState({ selectedTransaction, transactionVariables: selectedTransaction.variables });
    }
  };

  private onChangeTransactionVariable = (name: string) => {
    const { transactionVariables } = this.state;
    if (transactionVariables) {
      const selectedTransactionVariable = transactionVariables.find((variable: TransactionVariableJsonModel) => variable.name === name);
      this.setState({ selectedTransactionVariable });
    }
  };

  private uploadFile = async () => {
    const { file, selectedTransaction, selectedTransactionVariable } = this.state;
    const endpoint = `${apiUrl}/file/json`;
    if (selectedTransaction == undefined || selectedTransactionVariable == undefined || file == undefined) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transaction', selectedTransaction.name);
    formData.append('key', selectedTransactionVariable.name);
    try {
      await this.httpClient.execute(HttpMethod.POST, endpoint, formData, undefined, undefined, (progressEvent: ProgressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        this.setState({ percent: percentCompleted, progress: 'uploading' });
      });
    } catch (e) {
      console.error(e);
    }
  };

  private onChangeFile = (file: File | null) => {
    this.setState({ file });
  };
}

class State {
  selectedTransaction: TransactionJsonModel | undefined = undefined;
  selectedTransactionVariable: TransactionVariableJsonModel | undefined = undefined;
  transactionVariables: TransactionVariableJsonModel[] = [];
  percent: number = 0;
  progress: UploadProgress = 'idle';
  file: File | null = null;
}

interface Props {
  transactions: TransactionJsonModel[];
}

export default SandboxJsonContainer;
